// features/profile/edit/useProfileDraft.ts — the Edit Profile page's form state (1.27.70).
//
// One draft, seeded from the saved profile, saved with one PATCH. Only fields that
// changed are sent: an untouched privacy switch must never ride along with a bio
// edit and re-trigger anything server-side. The server re-checks everything
// (backend/apps/accounts/profile_fields.py) and refuses the whole save on any bad
// field, so validate() mirrors its rules to point at the field first.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isHttpUrl } from '@/core/lib/isHttpUrl';
import { birthdayProblem, hostnameOf, isWebAddress, normalizeUrl } from '@/core/lib/profileFields';
import {
  PROFILE_LIMITS,
  type BirthdayVisibility,
  type ProfileLink,
  type ProfileOverrides,
  type User,
} from '@/core/models/account';

export type Audience = 'everyone' | 'following' | 'nobody';

export interface ProfileDraft {
  displayName: string;
  bio: string;
  pronouns: string;
  creatorType: string;
  location: string;
  website: string;
  tagline: string;
  birthday: string;
  birthdayVisibility: BirthdayVisibility;
  links: ProfileLink[];
  /** Gradient choices, used whenever there is no uploaded image. */
  avatarKey: string;
  bannerTone: string;
  privateAccount: boolean;
  dmPolicy: Audience;
  interests: string[];
}

export interface MediaDraft {
  avatarFile: File | null;
  bannerFile: File | null;
  /** Drop the uploaded image and fall back to the gradient. */
  clearAvatarImage: boolean;
  clearBannerImage: boolean;
}

export type DraftErrors = Partial<Record<keyof ProfileDraft, string>>;

const NO_MEDIA: MediaDraft = {
  avatarFile: null,
  bannerFile: null,
  clearAvatarImage: false,
  clearBannerImage: false,
};

export function draftFromUser(user: User): ProfileDraft {
  return {
    displayName: user.name,
    bio: user.bio ?? '',
    pronouns: user.pronouns ?? '',
    creatorType: user.creatorType ?? '',
    location: user.location ?? '',
    website: user.link?.href ?? '',
    tagline: user.tagline ?? '',
    birthday: user.birthday ?? '',
    birthdayVisibility: user.birthdayVisibility ?? 'private',
    links: (user.links ?? []).map((l) => ({ label: l.label, href: l.href })),
    avatarKey: isHttpUrl(user.avatarKey) ? 'av-grad' : user.avatarKey || 'av-grad',
    bannerTone: isHttpUrl(user.banner) ? 'violet' : user.bannerTone || user.banner || 'violet',
    privateAccount: !!user.privateAccount,
    dmPolicy: user.dmPolicy ?? 'everyone',
    interests: [...(user.interests ?? [])],
  };
}

export function validateDraft(draft: ProfileDraft, today: Date = new Date()): DraftErrors {
  const errors: DraftErrors = {};
  if (!draft.displayName.trim()) errors.displayName = 'Add a display name.';
  else if (draft.displayName.trim().length > PROFILE_LIMITS.displayName) {
    errors.displayName = `Keep it to ${PROFILE_LIMITS.displayName} characters.`;
  }
  if (draft.bio.length > PROFILE_LIMITS.bio)
    errors.bio = `Keep it to ${PROFILE_LIMITS.bio} characters.`;
  if (draft.pronouns.trim().length > PROFILE_LIMITS.pronouns) {
    errors.pronouns = `Keep it to ${PROFILE_LIMITS.pronouns} characters.`;
  }
  if (draft.creatorType.trim().length > PROFILE_LIMITS.creatorType) {
    errors.creatorType = `Keep it to ${PROFILE_LIMITS.creatorType} characters.`;
  }
  if (draft.tagline.trim().length > PROFILE_LIMITS.tagline) {
    errors.tagline = `Keep it to ${PROFILE_LIMITS.tagline} characters.`;
  }
  const website = normalizeUrl(draft.website);
  if (website && !isWebAddress(website)) errors.website = 'Enter a web address, like example.org.';
  const birthday = birthdayProblem(draft.birthday, today);
  if (birthday) errors.birthday = birthday;
  if (draft.links.length > PROFILE_LIMITS.links) {
    errors.links = `You can add up to ${PROFILE_LIMITS.links} links.`;
  } else if (
    draft.links.some((l) => !isWebAddress(l.href) || l.label.length > PROFILE_LIMITS.linkLabel)
  ) {
    errors.links = 'One of your links is not a web address.';
  }
  return errors;
}

function sameLinks(a: ProfileLink[], b: ProfileLink[]): boolean {
  return (
    a.length === b.length && a.every((l, i) => l.label === b[i]?.label && l.href === b[i]?.href)
  );
}

/** The PATCH body for what changed between `saved` and `draft` (media excluded). */
export function changedFields(
  saved: ProfileDraft,
  draft: ProfileDraft,
  user: User,
): ProfileOverrides {
  const out: ProfileOverrides = {};
  const trimmed = (s: string) => s.trim();
  if (trimmed(draft.displayName) !== saved.displayName)
    out.displayName = trimmed(draft.displayName);
  if (draft.bio !== saved.bio) out.bio = draft.bio;
  if (trimmed(draft.pronouns) !== saved.pronouns) out.pronouns = trimmed(draft.pronouns);
  if (trimmed(draft.creatorType) !== saved.creatorType)
    out.creatorType = trimmed(draft.creatorType);
  if (trimmed(draft.location) !== saved.location) out.location = trimmed(draft.location);
  if (trimmed(draft.tagline) !== saved.tagline) out.tagline = trimmed(draft.tagline);
  const website = normalizeUrl(draft.website);
  if (website !== saved.website) {
    // Keep a label the member set earlier while the address is unchanged in host;
    // otherwise name the link after its site, as the old dialog did.
    const prev = user.link;
    const label =
      prev?.label && hostnameOf(prev.href) === hostnameOf(website)
        ? prev.label
        : hostnameOf(website);
    out.link = website ? { label, href: website } : null;
  }
  if (draft.birthday !== saved.birthday) out.birthday = draft.birthday || null;
  if (draft.birthdayVisibility !== saved.birthdayVisibility) {
    out.birthdayVisibility = draft.birthdayVisibility;
  }
  if (!sameLinks(draft.links, saved.links)) {
    out.links = draft.links.map((l) => ({ label: l.label.trim(), href: l.href.trim() }));
  }
  if (draft.avatarKey !== saved.avatarKey) out.avatarKey = draft.avatarKey;
  if (draft.bannerTone !== saved.bannerTone) out.bannerTone = draft.bannerTone;
  if (draft.privateAccount !== saved.privateAccount) out.privateAccount = draft.privateAccount;
  if (draft.dmPolicy !== saved.dmPolicy) out.dmPolicy = draft.dmPolicy;
  if (draft.interests.join(',') !== saved.interests.join(',')) out.interests = draft.interests;
  return out;
}

export function useProfileDraft(user: User) {
  const saved = useMemo(() => draftFromUser(user), [user]);
  const [draft, setDraft] = useState<ProfileDraft>(saved);
  const [media, setMedia] = useState<MediaDraft>(NO_MEDIA);

  const set = useCallback(<K extends keyof ProfileDraft>(key: K, value: ProfileDraft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  }, []);

  const reset = useCallback(() => {
    setDraft(saved);
    setMedia(NO_MEDIA);
  }, [saved]);

  // Object URLs for chosen files, revoked when replaced or on unmount.
  const avatarPreview = useMemo(
    () => (media.avatarFile ? URL.createObjectURL(media.avatarFile) : null),
    [media.avatarFile],
  );
  const bannerPreview = useMemo(
    () => (media.bannerFile ? URL.createObjectURL(media.bannerFile) : null),
    [media.bannerFile],
  );
  useEffect(
    () => () => void (avatarPreview && URL.revokeObjectURL(avatarPreview)),
    [avatarPreview],
  );
  useEffect(
    () => () => void (bannerPreview && URL.revokeObjectURL(bannerPreview)),
    [bannerPreview],
  );

  const changes = useMemo(() => {
    const out = changedFields(saved, draft, user);
    // While an image is set the server only echoes the image URL, not the
    // gradient underneath, so dropping the image must send the gradient too.
    if (media.clearAvatarImage) out.avatarKey = draft.avatarKey;
    if (media.clearBannerImage) out.bannerTone = draft.bannerTone;
    return out;
  }, [saved, draft, user, media.clearAvatarImage, media.clearBannerImage]);
  const dirty =
    Object.keys(changes).length > 0 ||
    media.avatarFile !== null ||
    media.bannerFile !== null ||
    media.clearAvatarImage ||
    media.clearBannerImage;

  /** What the avatar and banner show right now: a chosen file, the saved image, or the gradient. */
  const savedAvatarImage = isHttpUrl(user.avatarKey) ? user.avatarKey : null;
  const savedBannerImage = isHttpUrl(user.banner) ? (user.banner ?? null) : null;
  const avatarShown =
    avatarPreview ?? (media.clearAvatarImage ? null : savedAvatarImage) ?? draft.avatarKey;
  const bannerShown =
    bannerPreview ?? (media.clearBannerImage ? null : savedBannerImage) ?? draft.bannerTone;
  const hasAvatarImage = avatarShown !== draft.avatarKey;
  const hasBannerImage = bannerShown !== draft.bannerTone;

  return {
    saved,
    draft,
    set,
    media,
    setMedia,
    reset,
    changes,
    dirty,
    avatarShown,
    bannerShown,
    hasAvatarImage,
    hasBannerImage,
  };
}

export type ProfileDraftState = ReturnType<typeof useProfileDraft>;
